/**
 * SessionTimeoutWarning — modal shown to the user ~2 minutes before idle logout.
 *
 * Drop this component once, at the root of each portal's App, and wire it to
 * the values returned by useIdleTimeout.  It renders nothing while isWarning
 * is false, so it is cheap to mount unconditionally.
 *
 * Props:
 *   isWarning       {boolean}  — show the modal
 *   countdown       {number}   — seconds remaining until logout
 *   onExtend        {function} — "Stay logged in" callback
 *   onLogout        {function} — "Logout now" callback
 *   portalName      {string}   — displayed in the modal title (optional)
 */

import React, { useEffect, useRef } from 'react';

const styles = {
  overlay: {
    position: 'fixed',
    inset: 0,
    background: 'rgba(0,0,0,0.55)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 99999,
    fontFamily: 'system-ui, -apple-system, sans-serif',
  },
  card: {
    background: '#fff',
    borderRadius: 12,
    padding: '32px 36px',
    maxWidth: 420,
    width: '90vw',
    boxShadow: '0 20px 60px rgba(0,0,0,0.25)',
    textAlign: 'center',
    animation: 'ftw-fadein 0.18s ease',
  },
  icon: { fontSize: 40, marginBottom: 12 },
  title: {
    fontSize: 18,
    fontWeight: 700,
    color: '#1e293b',
    margin: '0 0 8px',
  },
  body: {
    fontSize: 14,
    color: '#475569',
    margin: '0 0 20px',
    lineHeight: 1.5,
  },
  countdownBox: {
    display: 'inline-block',
    background: '#fef2f2',
    border: '1px solid #fca5a5',
    borderRadius: 8,
    padding: '10px 24px',
    margin: '0 0 24px',
  },
  countdownNum: {
    fontSize: 36,
    fontWeight: 800,
    color: '#dc2626',
    lineHeight: 1,
    display: 'block',
  },
  countdownLabel: { fontSize: 11, color: '#ef4444', textTransform: 'uppercase', letterSpacing: 1 },
  actions: { display: 'flex', gap: 10, justifyContent: 'center', flexWrap: 'wrap' },
  btnStay: {
    padding: '10px 24px',
    borderRadius: 8,
    border: 'none',
    background: '#2563eb',
    color: '#fff',
    fontWeight: 700,
    fontSize: 14,
    cursor: 'pointer',
    flex: '1 1 140px',
  },
  btnLogout: {
    padding: '10px 24px',
    borderRadius: 8,
    border: '1px solid #d1d5db',
    background: '#fff',
    color: '#374151',
    fontWeight: 600,
    fontSize: 14,
    cursor: 'pointer',
    flex: '1 1 120px',
  },
};

function formatCountdown(seconds) {
  if (seconds >= 60) {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m}:${String(s).padStart(2, '0')}`;
  }
  return `${seconds}s`;
}

export function SessionTimeoutWarning({
  isWarning,
  countdown,
  onExtend,
  onLogout,
  portalName,
}) {
  const stayBtnRef = useRef(null);

  // Auto-focus the "Stay logged in" button so keyboard users can dismiss easily
  useEffect(() => {
    if (isWarning) {
      setTimeout(() => stayBtnRef.current?.focus(), 50);
    }
  }, [isWarning]);

  if (!isWarning) return null;

  const label = portalName ? `${portalName} session` : 'Your session';

  return (
    <>
      {/* Inject keyframe animation once */}
      <style>{`
        @keyframes ftw-fadein { from { opacity:0; transform:scale(.96) } to { opacity:1; transform:scale(1) } }
      `}</style>
      <div
        style={styles.overlay}
        role="dialog"
        aria-modal="true"
        aria-labelledby="ftw-title"
        aria-describedby="ftw-body"
      >
        <div style={styles.card}>
          <div style={styles.icon}>⏱</div>
          <h2 id="ftw-title" style={styles.title}>Session expiring soon</h2>
          <p id="ftw-body" style={styles.body}>
            {label} will expire due to inactivity. You will be automatically
            logged out when the timer reaches zero.
          </p>
          <div style={styles.countdownBox}>
            <span style={styles.countdownNum}>{formatCountdown(countdown)}</span>
            <span style={styles.countdownLabel}>remaining</span>
          </div>
          <div style={styles.actions}>
            <button
              ref={stayBtnRef}
              style={styles.btnStay}
              onClick={onExtend}
              aria-label="Stay logged in"
            >
              Stay logged in
            </button>
            <button
              style={styles.btnLogout}
              onClick={onLogout}
              aria-label="Logout now"
            >
              Logout now
            </button>
          </div>
        </div>
      </div>
    </>
  );
}

export default SessionTimeoutWarning;
